/* eslint-disable no-console */
import type { TokenAudience, TokenUpdate } from '@lseg-ciam/auth/token';
import {
  // raw SDK streams on purpose: this rig shows un-relayed behaviour (no CDN)
  apiToken$,
  connectToken$,
  dataToken$,
  restrictedToken$,
  thirdpartyToken$,
} from '@lseg-ciam/auth/token';
import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { Observable, Subscription } from 'rxjs';
import { rigSharedStyles } from './heimdall-rig-styles.js';

const audiences: TokenAudience[] = [
  'api.ws',
  'data.ws',
  'restricted.ws',
  'thirdparty.ws',
  'connect.ws',
];

const streamByAudience: Record<TokenAudience, Observable<TokenUpdate>> = {
  ['api.ws']: apiToken$,
  ['data.ws']: dataToken$,
  ['restricted.ws']: restrictedToken$,
  ['thirdparty.ws']: thirdpartyToken$,
  ['connect.ws']: connectToken$,
};

@customElement('heimdall-rig-audience-sdk')
export class HeimdallRigAudienceSdk extends LitElement {
  _debugInstanceId = Math.floor(Math.random() * 0xfff)
    .toString(32)
    .toUpperCase();

  @state()
  _subscriptionByAudience: Partial<Record<TokenAudience, Subscription>> = {};

  @state()
  _updatesByAudience: Partial<Record<TokenAudience, TokenUpdate[]>> = {};

  onSubscriptionRequestChanged(audience: TokenAudience) {
    return (event: Event) => {
      const target = event.target as HTMLInputElement;
      const { checked } = target;

      if (checked) {
        this._subscriptionByAudience = {
          ...this._subscriptionByAudience,
          [audience]: streamByAudience[audience].subscribe({
            next: (update) => {
              const currentUpdates = this._updatesByAudience[audience] || [];
              this._updatesByAudience[audience] = [...currentUpdates, update];
              this._updatesByAudience = { ...this._updatesByAudience };
            },
            complete: () => {
              this.removeSubscription(audience);
            },
          }),
        };
      } else {
        this.removeSubscription(audience);
      }
    };
  }

  removeSubscription(audience: TokenAudience) {
    this._subscriptionByAudience[audience]?.unsubscribe();
    delete this._subscriptionByAudience[audience];
    this._updatesByAudience[audience] = [];
    this._updatesByAudience = { ...this._updatesByAudience };
    this._subscriptionByAudience = { ...this._subscriptionByAudience };
  }

  render() {
    return html`
      <div class="app">
        <h2>SDK (raw) · ${this._debugInstanceId}</h2>
        <ul class="operation-rig">
          ${audiences.map((audience) => {
            const active =
              !!this._subscriptionByAudience[audience] &&
              !this._subscriptionByAudience[audience]?.closed;
            return html`
              <li>
                <label class="${active ? 'active' : ''}">
                  <input
                    type="checkbox"
                    .checked=${active}
                    @change=${this.onSubscriptionRequestChanged(audience)}
                  />
                  Subscribe to token updates for ${audience}
                </label>
                <p>
                  ${(this._updatesByAudience[audience] || []).map((update) => {
                    return createUpdate(audience, update);
                  })}
                </p>
              </li>
            `;
          })}
        </ul>
      </div>
    `;
  }

  static get styles() {
    return [
      rigSharedStyles,
      css`
        .app {
          background-color: mistyrose;
        }
      `,
    ];
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'heimdall-rig-audience-sdk': HeimdallRigAudienceSdk;
  }
}

function createUpdate(audience: TokenAudience, tokenUpdate?: TokenUpdate) {
  if (!tokenUpdate) return;

  if ('isRealTime' in tokenUpdate) {
    const { isRealTime } = tokenUpdate;

    return createTokenPill(
      `T ∴ ${audience}·${tokenUpdate.token.slice(-4)} ${
        isRealTime ? `RT:📲` : `D:📞`
      }`,
      isRealTime ? 'realtime' : 'delayed'
    );
  }

  return createTokenPill(`T ∴ ${audience}·${tokenUpdate.token.slice(-4)} 🎫`);
}

function createTokenPill(token: string, extraClass = '') {
  return html`<span class="token ${extraClass}">${token}</span> `;
}
